/**
 * whiteboardRegistration — projection-aware re-capture.
 *
 * When you PROJECT a board onto a wall (WhiteboardProjectionView), draw new
 * boxes around it, and re-photograph the result, the camera sees the board from
 * some arbitrary angle and crop. The four L-shaped corner registration marks in
 * the projection give us four known points: in the board's own coordinate frame
 * they sit at the corners (0,0)→(1,1), and in the photo they land wherever the
 * camera caught them.
 *
 * Four point correspondences determine a planar homography (a 3×3 projective
 * transform). Inverting it lets us map every recognized shape's photo-space
 * position back into the board's exact coordinate frame — so a box drawn to the
 * right of the projection lands to the right of the existing items, regardless
 * of how the photo was framed. This is what makes the additive round-trip land
 * positions correctly instead of relative to the photo crop.
 *
 * Pure & deterministic; no DOM, no hardware.
 */

import type { WhiteboardPoint, SceneFiducials, WhiteboardScene } from './whiteboardImportService';

/** A 3×3 homography stored row-major as 9 numbers. */
export type Homography = [number, number, number, number, number, number, number, number, number];

/**
 * Solve for the homography H mapping the four `src` points to the four `dst`
 * points (src[i] → dst[i]). Returns null if the configuration is degenerate
 * (e.g. collinear points) and the linear system can't be solved.
 *
 * Uses the standard DLT setup: with h33 fixed to 1, each correspondence yields
 * two linear equations in the remaining 8 unknowns, giving an 8×8 system solved
 * by Gaussian elimination with partial pivoting.
 */
export function computeHomography(
  src: [WhiteboardPoint, WhiteboardPoint, WhiteboardPoint, WhiteboardPoint],
  dst: [WhiteboardPoint, WhiteboardPoint, WhiteboardPoint, WhiteboardPoint],
): Homography | null {
  // Build the 8×9 augmented matrix (8 equations, 8 unknowns + RHS).
  const A: number[][] = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = src[i];
    const { x: X, y: Y } = dst[i];
    // x-equation: h0*x + h1*y + h2 - h6*x*X - h7*y*X = X
    A.push([x, y, 1, 0, 0, 0, -x * X, -y * X, X]);
    // y-equation: h3*x + h4*y + h5 - h6*x*Y - h7*y*Y = Y
    A.push([0, 0, 0, x, y, 1, -x * Y, -y * Y, Y]);
  }

  const sol = solveLinearSystem(A);
  if (!sol) return null;
  return [sol[0], sol[1], sol[2], sol[3], sol[4], sol[5], sol[6], sol[7], 1];
}

/** Apply a homography to a point, performing the perspective divide. */
export function applyHomography(h: Homography, p: WhiteboardPoint): WhiteboardPoint {
  const denom = h[6] * p.x + h[7] * p.y + h[8];
  if (denom === 0 || !Number.isFinite(denom)) return p;
  return {
    x: (h[0] * p.x + h[1] * p.y + h[2]) / denom,
    y: (h[3] * p.x + h[4] * p.y + h[5]) / denom,
  };
}

/**
 * Given the fiducials a re-capture reported (photo-space), return a homography
 * that maps PHOTO coordinates into the board's coordinate frame, where the four
 * registration corners are the unit square (0,0)→(1,1). Returns null when the
 * marks are degenerate.
 */
export function buildPhotoToBoardHomography(f: SceneFiducials): Homography | null {
  const src: [WhiteboardPoint, WhiteboardPoint, WhiteboardPoint, WhiteboardPoint] = [
    f.topLeft, f.topRight, f.bottomRight, f.bottomLeft,
  ];
  const dst: [WhiteboardPoint, WhiteboardPoint, WhiteboardPoint, WhiteboardPoint] = [
    { x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 },
  ];
  return computeHomography(src, dst);
}

/**
 * Return a copy of `scene` with every node position remapped from photo space
 * into the board's coordinate frame using its fiducials. If the scene has no
 * fiducials or they're degenerate, the scene is returned unchanged (positions
 * stay in photo space, which is the previous behavior). Positions are clamped
 * to [0,1]; nodes landing outside that box are kept but pinned to the edge.
 */
export function registerScenePositions(scene: WhiteboardScene): WhiteboardScene {
  if (!scene.fiducials) return scene;
  const h = buildPhotoToBoardHomography(scene.fiducials);
  if (!h) return scene;
  return {
    ...scene,
    nodes: scene.nodes.map((n) => {
      const mapped = applyHomography(h, n.position);
      return {
        ...n,
        position: {
          x: clamp01(mapped.x),
          y: clamp01(mapped.y),
        },
      };
    }),
  };
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0.5;
  return Math.min(1, Math.max(0, n));
}

/**
 * Solve a square linear system given as an n×(n+1) augmented matrix using
 * Gaussian elimination with partial pivoting. Returns the solution vector, or
 * null if the matrix is singular.
 */
function solveLinearSystem(m: number[][]): number[] | null {
  const n = m.length;
  // Work on a copy so callers' arrays aren't mutated.
  const a = m.map((row) => row.slice());

  for (let col = 0; col < n; col++) {
    // Partial pivot: find the row with the largest magnitude in this column.
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(a[r][col]) > Math.abs(a[pivot][col])) pivot = r;
    }
    if (Math.abs(a[pivot][col]) < 1e-12) return null; // singular
    if (pivot !== col) [a[col], a[pivot]] = [a[pivot], a[col]];

    // Eliminate this column from all other rows.
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const factor = a[r][col] / a[col][col];
      if (factor === 0) continue;
      for (let c = col; c <= n; c++) a[r][c] -= factor * a[col][c];
    }
  }

  const x = new Array<number>(n);
  for (let i = 0; i < n; i++) x[i] = a[i][n] / a[i][i];
  return x;
}
